/**
 * CardTabGroup — sections/card-tab-group.liquid, blocks/_card-tab.liquid.
 *
 * Selecting a card cross-fades the matching image. The initially selected
 * card and image are already resolved in Liquid, so this element's only job
 * on load is to take over state; it never has to correct the served HTML.
 *
 * Pairing is by DOM order: each card contains its own image, so the two are
 * looked up together and can never drift out of sync.
 */
class BlfcCardTabGroup extends HTMLElement {
  connectedCallback() {
    // `connectedCallback` fires again whenever the element is moved or
    // re-appended, so setup has to be safe to repeat.
    this.teardown();

    this.tabs = Array.from(this.querySelectorAll(".blfc-card-tab"));
    if (!this.tabs.length) return;

    this.abort = new AbortController();
    const { signal } = this.abort;

    const initial = parseInt(this.dataset.initialTab || "1", 10) - 1;
    this.index = Math.min(
      Math.max(Number.isNaN(initial) ? 0 : initial, 0),
      this.tabs.length - 1,
    );
    this.autoplayMs = parseInt(this.dataset.autoplayMs || "0", 10) || 0;
    this.stopped = false;

    this.select(this.index);

    this.addEventListener("click", this.onClick.bind(this), { signal });

    // Autoplay must not run away with the page while someone is
    // tabbing through a card (WCAG 2.2.2).
    this.addEventListener("focusin", () => this.pause(), { signal });
    this.addEventListener("focusout", () => this.resume(), { signal });
    document.addEventListener(
      "visibilitychange",
      () => (document.hidden ? this.pause() : this.resume()),
      { signal },
    );

    this.resume();
  }

  disconnectedCallback() {
    this.teardown();
  }

  /**
   * The theme editor replaces a section's HTML wholesale on every block edit.
   * Without this the timer would keep running against detached DOM and a
   * merchant would accumulate one live timer per edit.
   */
  teardown() {
    this.pause();
    if (this.abort) this.abort.abort();
    this.abort = null;
  }

  select(i) {
    this.index = i;

    this.tabs.forEach((tab, n) => {
      const on = n === i;
      tab.classList.toggle("is-selected", on);

      const toggle = tab.querySelector(".blfc-card-tab__toggle");
      if (toggle) toggle.setAttribute("aria-pressed", String(on));

      const image = tab.querySelector(".blfc-card-tabs__image");
      if (image) image.classList.toggle("is-active", on);
    });
  }

  onClick(event) {
    const tab = event.target.closest(".blfc-card-tab");
    if (!tab || !this.contains(tab)) return;

    // The whole card is clickable, but a real control inside it keeps its own
    // behaviour — this is what stops the CTA from also changing tabs, so the
    // block needs no per-instance stopPropagation. Our own headline toggle is
    // the exception: it is the intended way to select the card.
    const control = event.target.closest(
      "a[href], button, input, select, textarea",
    );
    if (control && !control.classList.contains("blfc-card-tab__toggle")) return;

    this.stop();
    this.select(this.tabs.indexOf(tab));
  }

  /** A click cancels autoplay for good, per the spec. */
  stop() {
    this.stopped = true;
    this.pause();
  }

  pause() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  /**
   * A chained timeout rather than setInterval: a throttled background tab
   * makes setInterval fire a burst of catch-up ticks on return.
   */
  resume() {
    this.pause();
    if (this.stopped || !this.autoplayMs || this.prefersReducedMotion) return;

    this.timer = setTimeout(() => {
      this.select((this.index + 1) % this.tabs.length);
      this.resume();
    }, this.autoplayMs);
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
}

if (!customElements.get("blfc-card-tab-group")) {
  customElements.define("blfc-card-tab-group", BlfcCardTabGroup);
}

/**
 * Theme editor: follow the merchant's selection instead of letting the timer
 * pull the card out from under them. Document-level because the section is
 * re-rendered on edit, which would discard an element-level listener.
 */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener("shopify:block:select", (event) => {
    const tab = event.target.closest && event.target.closest(".blfc-card-tab");
    if (!tab) return;

    const host = tab.closest("blfc-card-tab-group");
    if (!host || typeof host.select !== "function") return;

    host.pause();
    host.select(host.tabs.indexOf(tab));
  });
}
